(ns practitest.frontend.milestone.events
  (:require
   [ajax.core :as ajax]
   [clojure.string :as str]
   [day8.re-frame.http-fx]
   [practitest.frontend.common-entities :refer [build-filter]] 
   [practitest.frontend.common.i18n :refer [get-translation]]
   [practitest.frontend.components.grid.common :as gc]
   [practitest.frontend.components.grid.localstorage :as grid-storage]
   [practitest.frontend.components.grid.server :as server]
   [practitest.frontend.components.notifications :as notifications]
   [practitest.frontend.components2.entity-form :as entity-form]
   [practitest.frontend.events :as events]
   [practitest.frontend.common-entities.metadata :as metadata]
   [practitest.frontend.routing.backend-routes :as broutes]
   [practitest.frontend.routing.router :as router]
   [practitest.frontend.subs :as subs]
   [practitest.frontend.milestones :as-alias milestones]
   [practitest.frontend.common.re-frame :refer [reg-load-data-events]]
   [re-frame.core :as re-frame]
   [tick.core :as t]))

(def milestone-form-id :milestone-main)
(def current-entity :milestone)
(def metadata (get metadata/entities-metadata current-entity))
(def index-route (:index (:routes metadata)))

(defn collect-form-params
  [form-state all-fields]
  (let [dirty-fields-state (into {}
                                 (comp (filter (fn [[field field-state]]
                                                 (or (:dirty? field-state)
                                                     ;; Pass default values to server too - new form scenario
                                                     (:default-value field-state)
                                                     ;; Always pass name field to satisfy backend checks
                                                     ;; TODO: fix this on backend
                                                     (= :name field))))
                                       (map (fn [[field field-state]]
                                              [field (:value field-state)])))
                                 (:fields form-state))

        custom-fields (filter #(:custom? (get all-fields %)) (keys dirty-fields-state))
        system-fields (filter #(:system? (get all-fields %)) (keys dirty-fields-state))
        ;; special case for :name field
        system-fields (conj system-fields :name)
        user-fields (filter #(= :user (:type (get all-fields %))) custom-fields)

        params {:milestone (merge
                            (select-keys dirty-fields-state system-fields)
                             ;; Tags support
                            (when (contains? dirty-fields-state :tags)
                              {:all_tags_list (str/join "," (:tags dirty-fields-state))}))

                :custom    (merge
                             ;; user fields will be overwritten by subsequent merge
                            (select-keys dirty-fields-state custom-fields)

                             ;; Users fields handling (backend expects user id as a string in custom fields)
                            (reduce (fn [acc key]
                                      (if (contains? dirty-fields-state key)
                                        (assoc acc key (str (or (:id (get dirty-fields-state key))
                                                                (get dirty-fields-state key))))
                                        acc))
                                    {}
                                    user-fields))
                :kanban    (:lane_id (:kanban_lane dirty-fields-state))}]
    params))

(re-frame/reg-event-fx
 ::save-milestone
 (fn [{db :db} [_ {:keys [close?
                          new?]
                   :as   options}]]
   (let [route (if new?
                 (broutes/get-route "milestones#create" {:p_id (subs/get-current-project-id db)})
                 (broutes/get-route "milestones#update" {:p_id (subs/get-current-project-id db)
                                                         :id   (subs/get-current-entity-id db :milestone)}))]
     {:db (update db :progress conj ::save-milestone)
      :http-xhrio
      {:method          (:method (:data route))
       :uri             (:path route)
       :params          (collect-form-params (get-in db [:form-states2 milestone-form-id])
                                             (:fields db))
       :format          (ajax/json-request-format)
       :response-format (ajax/json-response-format {:keywords? true})
       :on-success      [::milestone-saved options]
       :on-failure      (let [message (if new?
                                        (get-translation "milestone.notify.error-creating" "Error creating Milestone")
                                        (get-translation "milestone.notify.error-saving" "Error saving Milestone"))]
                          [::events/api-error ::save-milestone {:message message}])}})))

(re-frame/reg-event-fx
 ::milestone-saved
 (fn [{db :db} [_
                {:keys [close?
                        new?]
                 :as   options}
                response]]
   (let [entity-type :milestone
         entity (:entity response)
         id (:id entity)]

     {:db (-> db
              (update :progress disj ::save-milestone)
              (update-in [:entity entity-type]
                         (fn [v]
                           (assoc v
                                  :current entity
                                  :state nil
                                  :remote nil
                                  :editing? false))))

      :fx (if close?
             ;; Save and close - go to index page, no back navigation
            [[::events/redirect (broutes/get-url "milestones#index" {:p_id (subs/get-current-project-id db)})]
             [:dispatch [::milestones/refresh-charts id]]]
             ;; Just save
            [[:dispatch-n [[::notifications/success2
                            (if new?
                              (get-translation "milestone.notify.created" "Milestone was successfully created")
                              (get-translation "milestone.notify.saved" "Milestone was successfully saved"))
                            {:auto-close notifications/auto-close-success-timeout}]
                           [::entity-form/set-notification-state
                            milestone-form-id
                            {:status     "success"
                             :type       :saved
                             :updated-at (t/now)
                             :updated-by (:id (:current-user db))}]
                            ;; Back navigation doesn't really work here, so we're using replace-state
                           [::router/replace-state :practitest.frontend.milestones/edit {:milestone-id id}]]]
             [:dispatch [::milestones/refresh-charts id]]])})))

(re-frame/reg-event-fx
  ::deleted
  (fn [{db :db} [_ id]]
    {:db         (-> db
                     (update :milestones dissoc id))
     :dispatch-n [[::notifications/success2
                   (get-translation "milestone.notify.deleted" "Milestone was successfully deleted")
                   {:auto-close notifications/auto-close-success-timeout}]
                  [::entity-form/set-notification-state
                   milestone-form-id
                   {:status     "success"
                    :type       :saved
                    :updated-at (t/now)
                    :updated-by (:id (:current-user db))}]
                  [::router/replace-state index-route]]}))

(defn delete-milestone [{db :db} [_ id]]
  {:http-xhrio {:method          :delete
                :uri             (broutes/get-url "milestones#destroy"
                                                  {:p_id (subs/get-current-project-id db)
                                                   :id   id})
                :format          (ajax/json-request-format)
                :response-format (ajax/json-response-format {:keywords? true})
                :on-success      [::deleted id]
                :on-failure      [::events/api-error ::delete {}]}})

(re-frame/reg-event-fx ::delete delete-milestone)

(defn got-linked-entities [op state-key db [_ {:keys [data _per_page current_page total_count full traceability_type]}]]
  ;; TODO: generalize it for all grids? -> Partly done on issues traceability by adjusting to current entity
  (-> db
      ;; XXX: not sure this is right, it should be per entity?
      (when traceability_type
            (assoc-in db [:entity (:current-entity db) :traceability (keyword traceability_type)] data))
      (assoc-in [:entity (:current-entity db) :stale-grid?] false)
      (update-in [:entity (:current-entity db) state-key] assoc
                 :data data
                 :selection #{}
                 :current-page current_page
                 ;:per-page per_page
                 :total-count total_count
                 :full-data? (boolean full)
                 ;; Sync filters
                 ;:current-filter-fetched (get-in sb [:entity :milestone state-key :current-filter-selected])
                 )
      (update :progress disj op)))

;; NOTE: adopted from testset.events/got-tests
(defn got-entities [op state-key db [_ {:keys [data
                                               per_page
                                               current_page
                                               total_count
                                               full
                                               filter_columns]}]]
  (-> db
      ;; XXX: this is kind of ugly, but i didn't find a better way to handle it
      ;;      Basically, the issue is that we store selection as a set of IDs
      ;;      The selection is preserved even if the data is swapped when using server side pagination
      ;;      Now, if we need to know something besides the ID, for example to show the warning that
      ;;      the user selected some entities already linked to other milestone, it will not work if the data doesn't contain the selected entity
      ;;      It's not a big deal, it will not blow up the memory, it's just a big ugly
      ;; TODO: refactor it once we will get to "indexed entities" in `app-db`
      (update-in [:entity (:current-entity db) state-key :cache-entities]
                 (fn [entities]
                   (into (or entities {})
                         (map #(vector (:id %) %) data))))
      (update-in [:entity (:current-entity db) state-key] assoc
                 ;; Selection uses this
                 ;; TODO: refactor this hidden dependency
                 :data data
                 :current-page current_page
                 :per-page per_page
                 :total-count total_count
                 :full-data? (boolean full)
                 ;; Sync filters
                 :filter-cols filter_columns
                 :current-filter-fetched (get-in db [:entity (:current-entity db) state-key
                                                     :current-filter-selected]))
      (update :progress disj op)))

(defn- extract-grid-query
  "Given variadic event args, return the last map (grid-query) or {}." [ev-args]
  (reduce (fn [_ x] (when (map? x) x)) {} ev-args))

(defn fetch-entities [url-name state-key on-success {:keys [db]} [event-id & ev-args]]
  (let [grid-query (extract-grid-query ev-args)
        filter (get-in db [:entity (:current-entity db) state-key :current-filter-selected])
        query-map (build-filter filter)
        query-map (merge query-map grid-query)]
    {:db (update db :progress conj event-id)
     :fx [[:http-xhrio {:method :get
                        :uri (broutes/get-url url-name
                                              {:p_id (subs/get-current-project-id db)})
                        :params query-map
                        :format :url
                        :vec-strategy :rails
                        :response-format (ajax/json-response-format {:keywords? true})
                        :on-success [on-success]
                        :on-failure [::events/api-error event-id {}]}]]}))

(defn uris [entity]
  (cond
      (= entity :milestone) {:fetch-linked-entities "milestones#linked_entities"
                             :link-entities "milestones#link_entities"}
      (= entity :issue) {:fetch-linked-entities "issues#linked_entities"
                             :link-entities "issues#link_entities"}))

;; NOTE: adopted from testset.events/fetch-instances
(defn fetch-linked-entities [entity-type on-success {:keys [db]} [event-id & ev-args]]
  (let [grid-query (extract-grid-query ev-args)
        query-map (merge grid-query {:entity_type entity-type
                                     :timestamp (.getTime (js/Date.))
                                     :current_entity (:current-entity db)})
        current-entity (:current-entity db)]
    {:db (update db :progress conj event-id)
     :fx [[:http-xhrio {:method :get
                        :uri (broutes/get-url (:fetch-linked-entities (uris current-entity))
                                              {:p_id (subs/get-current-project-id db)
                                               :id (subs/get-current-entity-id db current-entity)})
                        ;; When we press back, chrome uses cache to retrieve results of
                        ;; this XHR, so we're adding "unique" timestamp to prevent it doing that
                        ;; TODO: handle this on backend ?
                        :params query-map
                        :response-format (ajax/json-response-format {:keywords? true})
                        :on-success [on-success]
                        :on-failure [::events/api-error event-id {}]}]]}))

(re-frame/reg-event-fx
  ::link-entities-succeeded
  (fn [{:keys [db]} [_ caller-id refresh-event hide-event _]]
    {:db (update db :progress disj caller-id)
     :fx (cond-> [[:dispatch refresh-event]
                  [:dispatch [::events/fetch-history]]
                  [:dispatch hide-event]]
           (= :milestone (:current-entity db))
           (conj [:dispatch [::milestones/refresh-charts
                             (subs/get-current-entity-id db :milestone)]]))}))

(re-frame/reg-event-fx
 ::link-entities-failed
 (fn [{:keys [db]} [_ caller-id hide-event result]]
   (let [message (->> (get-in result [:response :errors])
                      (cons "Failed to link entities.")
                      (str/join "\n"))]
     {:db (update db :progress disj caller-id)
      :fx [[:dispatch [::events/api-error caller-id {:message message}]]
           [:dispatch hide-event]]})))


(defn link-entities! [entity-type refresh-event hide-event {:keys [db]} [event-id entity-ids]]
  (let [current-entity (:current-entity db)]
  {:db (update db :progress conj event-id)
   :fx [[:http-xhrio {:method :post
                      :uri (broutes/get-url (:link-entities (uris current-entity))
                                            {:p_id (subs/get-current-project-id db)
                                             :id (subs/get-current-entity-id db current-entity)})
                      :format (ajax/json-request-format)
                      :params {:entity_type entity-type
                               :entity_ids entity-ids
                               :current_entity current-entity}
                      :response-format (ajax/json-response-format {:keywords? true})
                      :on-success [::link-entities-succeeded event-id refresh-event hide-event]
                      :on-failure [::link-entities-failed event-id hide-event]}]]}))

(re-frame/reg-event-fx
  ::unlink-entities-succeeded
  (fn [{:keys [db]} [_ caller-id refresh-event _]]
    {:db (update db :progress disj caller-id)
     :fx (cond-> [[:dispatch refresh-event]
                  [:dispatch [::events/fetch-history]]]
           (= :milestone (:current-entity db))
           (conj [:dispatch [::milestones/refresh-charts
                             (subs/get-current-entity-id db :milestone)]]))}))

(re-frame/reg-event-fx
 ::unlink-entities-failed
 (fn [{:keys [db]} [_ caller-id _]]
   {:db (update db :progress disj caller-id)
    :fx [[:dispatch [::events/api-error caller-id {:message "Failed to unlink entities"}]]]}))

(defn unlink-entities! [entity-type refresh-event {:keys [db]} [event-id entity-ids]]
  {:db (update db :progress conj event-id)
   :fx [[:http-xhrio {:method :post
                      :uri (broutes/get-url "milestones#unlink_entities"
                                            {:p_id (subs/get-current-project-id db)
                                             :id (subs/get-current-entity-id db :milestone)})
                      :format (ajax/json-request-format)
                      :params {:entity_type entity-type
                               :entity_ids entity-ids}
                      :response-format (ajax/json-response-format {:keywords? true})
                      :on-success [::unlink-entities-succeeded event-id refresh-event]
                      :on-failure [::unlink-entities-failed event-id]}]]})

(re-frame/reg-event-db
 ::got-linked-requirements
 (partial got-linked-entities ::fetch-linked-requirements :requirements-state))

(re-frame/reg-event-db
 ::got-requirements
 (partial got-entities ::fetch-requirements :link-requirements-state))

(re-frame/reg-event-fx
 ::fetch-linked-requirements
 (partial fetch-linked-entities :requirements ::got-linked-requirements))

(re-frame/reg-event-fx
 ::fetch-requirements
 (partial fetch-entities "requirements#list2" :link-requirements-state ::got-requirements))

(re-frame/reg-event-fx
 ::link-requirements
 (partial link-entities! :requirements [::fetch-linked-requirements] [::hide-link-entities-modal :link-requirements-state]))

(re-frame/reg-event-fx
 ::unlink-requirements
 (partial unlink-entities! :requirements [::fetch-linked-requirements]))

(re-frame/reg-event-db
 ::got-linked-testsets
 (partial got-linked-entities ::fetch-linked-testsets :testsets-state))

(re-frame/reg-event-db
 ::got-testsets
 (partial got-entities ::fetch-testsets :link-testsets-state))

(re-frame/reg-event-fx
 ::fetch-linked-testsets
 (partial fetch-linked-entities :testsets ::got-linked-testsets))

(re-frame/reg-event-fx
 ::fetch-testsets
 (partial fetch-entities "suites#list2" :link-testsets-state ::got-testsets))

(re-frame/reg-event-fx
 ::link-testsets
 (partial link-entities! :testsets [::fetch-linked-testsets] [::hide-link-entities-modal :link-testsets-state]))

(re-frame/reg-event-fx
 ::unlink-testsets
 (partial unlink-entities! :testsets [::fetch-linked-testsets]))

(re-frame/reg-event-db
 ::got-linked-issues
 (partial got-linked-entities ::fetch-linked-issues :issues-state))

(re-frame/reg-event-db
 ::got-issues
 (partial got-entities ::fetch-issues :link-issues-state))

(re-frame/reg-event-fx
 ::fetch-linked-issues
 (partial fetch-linked-entities :issues ::got-linked-issues))

(re-frame/reg-event-fx
 ::fetch-issues
 (partial fetch-entities "issues#list2" :link-issues-state ::got-issues))

(re-frame/reg-event-fx
 ::link-issues
 (partial link-entities! :issues [::fetch-linked-issues] [::hide-link-entities-modal :link-issues-state]))

(re-frame/reg-event-fx
 ::unlink-issues
 (partial unlink-entities! :issues [::fetch-linked-issues]))

(re-frame/reg-event-db
  ::got-linked-milestones
  (partial got-linked-entities ::fetch-linked-milestones :milestones-state))

(re-frame/reg-event-db
  ::got-milestones
  (partial got-entities ::fetch-milestones :link-milestones-state))

(re-frame/reg-event-fx
  ::fetch-linked-milestones
  (partial fetch-linked-entities :milestones ::got-linked-milestones))

(re-frame/reg-event-fx
  ::fetch-milestones
  (partial fetch-entities "milestones#list2" :link-milestones-state ::got-milestones))

(re-frame/reg-event-fx
  ::link-milestones
  (partial link-entities! :milestones [::fetch-linked-milestones] [::hide-link-entities-modal :link-milestones-state]))

(re-frame/reg-event-fx
  ::unlink-milestones
  (partial unlink-entities! :milestones [::fetch-linked-milestones]))

(re-frame/reg-event-db
 ::hide-link-entities-modal
 (fn [db [_ state-key]]
   (assoc-in db [:entity (:current-entity db) state-key :show-link-entities-modal?] false)))

(re-frame/reg-event-db
  ::show-milestones-by-status
  (fn [db [_ statuses]]
    (let [values (set (map :id statuses))
          project-id (subs/get-current-project-id db)]
      (grid-storage/save-milestone-index-filtering project-id values)
      (assoc-in db [:entity :milestone :show-by-status] values))))

;; adopted from testset.events/set-current-filter
(defn set-link-entities-dialog-grid-filter [{:keys [db]}
                                            [_ state-key fetch-event grid-state filter]]
  {:db (assoc-in db [:entity (:current-entity db) state-key :current-filter-selected] filter)
   :dispatch-n [[fetch-event
                 filter
                 ;; Reuse filter, reset pages counter
                 ;; TODO: something better ?
                 (server/build-query {:local-state-updating grid-state} 1)]]})

(re-frame/reg-event-fx
 ::set-link-entities-dialog-grid-filter
 set-link-entities-dialog-grid-filter)

(defn enrich-milestone [_evt {:keys [id milestone-status archived]
                              :as   milestone}]
  (let [status (if archived "Archived" milestone-status)]
    (merge milestone {:milestone/id        id
                      :milestone/archived? archived
                      :milestone/status    status
                      :milestone/upcoming? (= milestone-status "Upcoming")
                      :milestone_status    status})))

(letfn [(make-evt [evt]
          {:evt       evt
           :db-path   [:milestones]
           :transform enrich-milestone
           :payload   :entity
           :progress  ::save-milestone
           :track     ["Milestones" {"Action" (name evt)}]})]
  (reg-load-data-events (make-evt ::archive)
                        (make-evt ::unarchive)))

(comment
  (require '[re-frame.db])
  (tap> re-frame.db/app-db)

  (re-frame/dispatch [::fetch-linked-requirements
                      nil
                      (merge gc/default-query {:per_page gc/default-per-page})]))
